// Hook: Algo engine — strategy execution, signals, backtest
import { useState, useCallback, useRef, useEffect } from 'react';
import { useTrading } from '../context/TradingContext';
import { doc, setDoc, onSnapshot, collection, addDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../firebase';

export function useAlgoEngine() {
  const { connectionStatus, placeOrder, getQuote } = useTrading();
  const [strategies, setStrategies] = useState([]);
  const [signals, setSignals] = useState([]);
  const [execLog, setExecLog] = useState([]);
  const [backtestResults, setBacktestResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const intervalRef = useRef(null);

  // Load strategies from Firestore
  useEffect(() => {
    const user = auth.currentUser;
    if (!user) { setLoading(false); return; }

    const ref = collection(db, 'users', user.uid, 'algoStrategies');
    const unsub = onSnapshot(ref, (snap) => {
      setStrategies(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    return unsub;
  }, []);

  // Load execution log
  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;
    const ref = collection(db, 'users', user.uid, 'algoExecutionLog');
    const unsub = onSnapshot(ref, (snap) => {
      setExecLog(snap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) =>
        (b.timestamp || '') > (a.timestamp || '') ? 1 : -1
      ));
    });
    return unsub;
  }, []);

  const saveStrategy = useCallback(async (strategy) => {
    const user = auth.currentUser;
    if (!user) return;

    if (strategy.id) {
      const ref = doc(db, 'users', user.uid, 'algoStrategies', strategy.id);
      await setDoc(ref, { ...strategy, updatedAt: serverTimestamp() }, { merge: true });
    } else {
      const ref = collection(db, 'users', user.uid, 'algoStrategies');
      await addDoc(ref, { ...strategy, createdAt: serverTimestamp(), updatedAt: serverTimestamp() });
    }
  }, []);

  const deleteStrategy = useCallback(async (strategyId) => {
    const user = auth.currentUser;
    if (!user) return;
    await deleteDoc(doc(db, 'users', user.uid, 'algoStrategies', strategyId));
  }, []);

  const logExecution = useCallback(async (entry) => {
    const user = auth.currentUser;
    if (!user) return;
    const ref = collection(db, 'users', user.uid, 'algoExecutionLog');
    await addDoc(ref, { ...entry, timestamp: new Date().toISOString() });
  }, []);

  // Evaluate strategy conditions against current market data
  const evaluateConditions = useCallback((conditions, instrument) => {
    const quote = getQuote(instrument.exchange, instrument.token);
    if (!quote) return false;

    return conditions.every(cond => {
      const value = quote[cond.field] || quote.ltp;
      switch (cond.operator) {
        case 'is above': return value > parseFloat(cond.value);
        case 'is below': return value < parseFloat(cond.value);
        case 'crosses above': return value >= parseFloat(cond.value);
        case 'crosses below': return value <= parseFloat(cond.value);
        default: return false;
      }
    });
  }, [getQuote]);

  // Run strategy engine
  const startEngine = useCallback((activeStrategies) => {
    if (connectionStatus !== 'connected' || running) return;
    setRunning(true);

    intervalRef.current = setInterval(() => {
      activeStrategies.forEach(strategy => {
        if (!strategy.enabled || !strategy.instrument) return;

        const triggered = evaluateConditions(strategy.conditions || [], strategy.instrument);
        if (triggered) {
          const signal = {
            strategyId: strategy.id,
            strategyName: strategy.name,
            action: strategy.action,
            instrument: strategy.instrument,
            timestamp: new Date().toISOString(),
          };

          setSignals(prev => [signal, ...prev].slice(0, 100));

          // Auto-execute if enabled
          if (strategy.autoExecute) {
            const orderData = {
              variety: 'NORMAL',
              tradingsymbol: strategy.instrument.symbol,
              symboltoken: strategy.instrument.token,
              transactiontype: strategy.action === 'BUY' || strategy.action === 'EXIT_SHORT' ? 'BUY' : 'SELL',
              exchange: strategy.instrument.exchange,
              ordertype: 'MARKET',
              producttype: strategy.productType || 'INTRADAY',
              quantity: String(strategy.quantity || 1),
              duration: 'DAY',
            };

            placeOrder(orderData).then(result => {
              logExecution({
                ...signal,
                orderId: result?.data?.orderid,
                status: result?.status ? 'executed' : 'failed',
                message: result?.message,
              });
            });
          } else {
            logExecution({ ...signal, status: 'signal_only' });
          }
        }
      });
    }, 5000); // Check every 5 seconds
  }, [connectionStatus, running, evaluateConditions, placeOrder, logExecution]);

  const stopEngine = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setRunning(false);
  }, []);

  // Simple backtest engine
  const runBacktest = useCallback(async (strategy, candles) => {
    if (!candles || candles.length === 0) return null;

    const trades = [];
    let position = null;
    let capital = 100000;
    let maxCapital = capital;
    let maxDrawdown = 0;

    candles.forEach((candle, i) => {
      if (i === 0) return;

      const mockQuote = { ltp: candle.close, open: candle.open, high: candle.high, low: candle.low, volume: candle.volume };
      const prev = candles[i - 1];

      // Simplified condition evaluation for backtest
      const triggered = (strategy.conditions || []).every(cond => {
        const val = mockQuote[cond.field] || mockQuote.ltp;
        switch (cond.operator) {
          case 'is above': return val > parseFloat(cond.value);
          case 'is below': return val < parseFloat(cond.value);
          case 'crosses above': return val >= parseFloat(cond.value) && (prev[cond.field] || prev.close) < parseFloat(cond.value);
          case 'crosses below': return val <= parseFloat(cond.value) && (prev[cond.field] || prev.close) > parseFloat(cond.value);
          default: return false;
        }
      });

      if (triggered && !position) {
        position = { entryPrice: candle.close, entryTime: candle.time, qty: strategy.quantity || 1 };
      } else if (position && !triggered) {
        const pnl = (candle.close - position.entryPrice) * position.qty;
        capital += pnl;
        maxCapital = Math.max(maxCapital, capital);
        const dd = ((maxCapital - capital) / maxCapital) * 100;
        maxDrawdown = Math.max(maxDrawdown, dd);

        trades.push({
          entry: position.entryPrice,
          exit: candle.close,
          entryTime: position.entryTime,
          exitTime: candle.time,
          pnl,
          qty: position.qty,
        });
        position = null;
      }
    });

    const wins = trades.filter(t => t.pnl > 0).length;
    const results = {
      totalTrades: trades.length,
      wins,
      losses: trades.length - wins,
      winRate: trades.length > 0 ? (wins / trades.length) * 100 : 0,
      totalPnL: trades.reduce((s, t) => s + t.pnl, 0),
      maxDrawdown,
      finalCapital: capital,
      trades,
    };

    setBacktestResults(results);
    return results;
  }, []);

  // Cleanup
  useEffect(() => {
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  return {
    strategies,
    signals,
    execLog,
    backtestResults,
    loading,
    running,
    saveStrategy,
    deleteStrategy,
    startEngine,
    stopEngine,
    runBacktest,
  };
}
